import React from 'react';
import TrialSignupForm from '../components/home/TrialSignupForm';
import { useTrialOffer } from '@shared/ui/lib/useTrialOffer';
import { trialCopy } from '../utils/trialCopy';
import { usePageDocumentTitle } from '../hooks/useDocumentTitle';

/* ------------------------------------------------------------------ */
/* Inline SVG icons                                                     */
/* ------------------------------------------------------------------ */

const ChartIcon: React.FC = () => (
  <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);

const ClockIcon: React.FC = () => (
  <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const StarIcon: React.FC = () => (
  <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

const ShieldIcon: React.FC = () => (
  <svg className="w-[18px] h-[18px] text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
  </svg>
);

/* ------------------------------------------------------------------ */
/* Component                                                            */
/* ------------------------------------------------------------------ */

const FreeTrialPage: React.FC = () => {
  usePageDocumentTitle('Free Trial');
  const { trialDays } = useTrialOffer();
  const copy = trialCopy(trialDays);

  return (
    <div className="min-h-screen bg-[#0a0f1a] flex flex-col font-sans">
      {/* ---- Main content ---- */}
      <main className="flex-1 flex items-center justify-center px-5 py-12">
        <div className="w-full max-w-[1120px] grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">

          {/* ===== Left column ===== */}
          <div className="flex flex-col gap-8">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 bg-gray-900 border border-[#1e3a5f] rounded-full px-3.5 py-1.5 w-fit"
              role="note"
            >
              <span className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" aria-hidden="true" />
              <span className="text-[11px] font-bold tracking-widest text-blue-300 uppercase">
                {copy.badge}
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-[clamp(2rem,4vw,3rem)] font-extrabold leading-tight text-white m-0">
              Premium Racing{' '}
              <span className="text-racing-green">Intelligence.</span>
            </h1>

            {/* Subtext */}
            <p className="text-base leading-relaxed text-gray-400 max-w-[480px] m-0">
              Advanced analytics and data-driven insights built for serious racing
              professionals across all Australian venues.
            </p>

            {/* Feature list */}
            <ul className="flex flex-col gap-4 list-none m-0 p-0" aria-label="Platform features">
              {[
                { icon: <ChartIcon />, text: 'Advanced analytics across all race venues' },
                { icon: <ClockIcon />, text: 'Real-time updates and live race data' },
                { icon: <StarIcon />,  text: 'Expert insights from industry professionals' },
                { icon: <ShieldIcon />, text: 'Proven results — no credit card required' },
              ].map(({ icon, text }) => (
                <li key={text} className="flex items-center gap-3.5">
                  <div className="flex-shrink-0 w-9 h-9 bg-gray-900 rounded-lg flex items-center justify-center">
                    {icon}
                  </div>
                  <span className="text-[15px] text-gray-300 leading-snug">{text}</span>
                </li>
              ))}
            </ul>

            {/* Tagline */}
            <p className="text-[13px] text-gray-500 tracking-wide m-0">
              {trialDays
                ? <>No credit card &nbsp;·&nbsp; Cancel anytime &nbsp;·&nbsp; Full access from day one</>
                : <>Free account &nbsp;·&nbsp; No credit card to join &nbsp;·&nbsp; Cancel anytime</>}
            </p>
          </div>

          {/* ===== Right column — signup card ===== */}
          <TrialSignupForm />
        </div>
      </main>

      {/* ---- Stats bar ---- */}
      <footer
        className="bg-[#0d1520] border-t border-[#1f2937] py-7 px-5"
        aria-label="Platform statistics"
      >
        <div className="max-w-[1120px] mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { number: '10K+',   label: 'Racing professionals' },
            { number: '98%',    label: 'Satisfaction rate' },
            { number: 'All AU', label: 'Race venues covered' },
            { number: 'Live',   label: 'Real-time updates' },
          ].map(({ number, label }) => (
            <div key={label}>
              <span className="block text-2xl font-extrabold text-racing-green leading-tight">{number}</span>
              <span className="block text-[13px] text-gray-500 mt-1">{label}</span>
            </div>
          ))}
        </div>
      </footer>
    </div>
  );
};

export default FreeTrialPage;
